import { useState } from 'react';
import { motion } from 'motion/react';
import { useEffect } from 'react';
import { Bell, PartyPopper, Users } from 'lucide-react';
import { GAME_IDS, type GameId } from '@gmp/shared-types/browser';
import { Logo, Sheet, Stage } from '@gmp/shared-ui';
import { gameInfo } from '../app/catalog';
import { HowTo } from './HowTo';
import { navigate } from '../app/router';
import { sound } from '../app/prefs';
import { usePartyState } from '../App';
import { party } from '../net/client';
import { GameCard, GameSections, PlannedCard } from './GameCard';
import { PLANNED_GAMES } from '../app/upcoming';
import { useEnabledGames } from '../app/serverConfig';
import { SiteFooter } from '../app/Footer';
import { AccountEntry } from './Account';

export function Home() {
  const { leftReason, status, parties } = usePartyState();
  useEffect(() => {
    if (status === 'online') party.myParties();
  }, [status]);
  const turns = parties?.filter((p) => p.yourTurn && p.stage === 'IN_GAME').length ?? 0;
  const enabled = useEnabledGames();
  const [open, setOpen] = useState<GameId | null>(null);
  const go = (r: Parameters<typeof navigate>[0]) => {
    sound.play('tap');
    party.clearError();
    navigate(r);
  };
  return (
    <Stage>
      <main className="flex-1 flex flex-col items-center gap-6 safe-pad safe-top safe-bottom pt-4 pb-10 max-w-lg w-full mx-auto">
        {/* Social (friends and open games) and Sign in / your account, always in reach at the top. */}
        <div className="w-full flex items-center justify-between gap-2 min-h-10">
          <button className="btn btn-sm btn-secondary" onClick={() => go({ name: 'friends' })} data-testid="home-social">
            <Users size={18} aria-hidden /> Social
          </button>
          <AccountEntry />
        </div>
        {/* The party picture, edge to edge (out past the page padding), with the logo over its bottom fifth: a
            negative top margin in % is a share of the width, and 11.25% of the width is 20% of a 16:9 picture. */}
        <div className="relative -mt-2 flex flex-col items-center" style={{ marginInline: 'calc(-1 * max(16px, env(safe-area-inset-left)))', width: 'calc(100% + 2 * max(16px, env(safe-area-inset-left)))' }} data-testid="home-hero">
          <div className="relative w-full">
            <img
              src="/brand-hero.webp"
              alt="Clay party characters on a couch, cheering at their phones in front of a TV"
              width={1600}
              height={900}
              fetchPriority="high"
              className="block w-full h-auto"
            />
            <div aria-hidden className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[var(--stage)] to-transparent" />
          </div>
          <motion.div initial={{ scale: 0.8, rotate: -4, opacity: 0 }} animate={{ scale: 1, rotate: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 180, damping: 12 }} className="relative" style={{ marginTop: '-11.25%' }}>
            <Logo size="lg" />
          </motion.div>
        </div>
        {leftReason && leftReason !== 'left' && (
          <p className="chip bg-bad/20 border-bad text-sm py-2 px-4" role="status">
            {leftReason === 'kicked' ? 'The host removed you from that party.' : leftReason === 'denied' ? 'The host declined your request to join.' : 'That party has ended.'}
          </p>
        )}
        <div className="grid gap-4 w-full">
          <div className="grid grid-cols-2 gap-3">
            <button className="btn min-h-20 px-2 whitespace-nowrap text-[clamp(1.1rem,5.6vw,1.6rem)]" onClick={() => go({ name: 'create' })} data-testid="home-create">
              Create Party
            </button>
            <button className="btn btn-gold min-h-20 px-2 whitespace-nowrap text-[clamp(1.1rem,5.6vw,1.6rem)]" onClick={() => go({ name: 'join' })} data-testid="home-join">
              Join Party
            </button>
          </div>
          {parties && parties.length > 0 && (
            <button className={`btn ${turns ? 'btn-gold' : 'btn-secondary'} text-lg`} onClick={() => go({ name: 'parties' })} data-testid="home-parties">
              <Bell size={22} /> {turns ? `Your turn in ${turns} ${turns === 1 ? 'party' : 'parties'}` : `Your parties (${parties.length})`}
            </button>
          )}
          <div className="grid grid-cols-2 gap-4">
            <button className="btn btn-secondary px-2 whitespace-nowrap text-[clamp(0.9rem,4.2vw,1.1rem)]" onClick={() => go({ name: 'how' })}>
              How to Play
            </button>
            <button className="btn btn-secondary px-2 whitespace-nowrap text-[clamp(0.9rem,4.2vw,1.1rem)]" onClick={() => go({ name: 'settings' })}>
              Settings
            </button>
          </div>
        </div>
        <section className="w-full flex flex-col gap-3" aria-label="Games">
          <GameSections ids={GAME_IDS} enabled={enabled} card={(id) => <GameCard key={id} id={id} enabled={enabled.includes(id)} onClick={() => { sound.play('select'); setOpen(id); }} />} />
          {PLANNED_GAMES.length > 0 && <h2 className="font-display font-black text-xl text-mute mt-4">Coming soon</h2>}
          {PLANNED_GAMES.map((g) => <PlannedCard key={g.id} game={g} compact />)}
        </section>
        <a href="/privacy" onClick={(e) => { e.preventDefault(); go({ name: 'privacy' }); }} className="text-sm text-mute underline underline-offset-4">Privacy: accounts optional, no ads, media auto-deleted</a>
        <p className="text-xs text-mute/70">{status === 'online' ? 'Connected' : status === 'connecting' ? 'Connecting to the game server…' : 'Game server offline. Retrying…'}</p>
        <SiteFooter className="-mt-4" />
      </main>
      <Sheet open={open !== null} onClose={() => setOpen(null)} title={open ? gameInfo(open).name : ''}>
        {open && (
          <div className="grid gap-4" data-testid="game-sheet">
            <GameCard id={open} compact />
            <p className="text-mute">{gameInfo(open).description}</p>
            <button className="btn text-xl min-h-16" onClick={() => { setOpen(null); go({ name: 'create', game: open }); }} data-testid="game-sheet-create">
              <PartyPopper /> Start a party with {gameInfo(open).name}
            </button>
            <HowTo gameId={open} />
          </div>
        )}
      </Sheet>
    </Stage>
  );
}
